import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LoaderService } from '../app-loader/loader.service';
import { ProjectService } from '../project/project.service';
import { SiteVisitPdfService } from './site-visit-pdf.service';

export type PdfJobToastStatus = 'hidden' | 'preparing' | 'success' | 'failed';

export interface PdfJobToastState {
  status: PdfJobToastStatus;
  message: string;
  downloadUrl: string;
}

export type SiteVisitPdfUrlAction = 'download' | 'preview';

export interface SiteVisitPdfJobOptions {
  preparingMessage?: string;
  fileName?: string;
  /** When the API returns a URL immediately (sync). */
  immediateUrlAction?: SiteVisitPdfUrlAction;
  /** When the job completes after polling. */
  polledSuccessAction?: SiteVisitPdfUrlAction;
  onPreviewUrl?: (url: string) => void;
  retry: () => Observable<unknown>;
}

const HIDDEN_TOAST: PdfJobToastState = {
  status: 'hidden',
  message: '',
  downloadUrl: '',
};

@Injectable({
  providedIn: 'root',
})
export class SiteVisitPdfJobService implements OnDestroy {
  readonly toast$ = new BehaviorSubject<PdfJobToastState>(HIDDEN_TOAST);

  private pollSub?: Subscription;
  private activeOptions?: SiteVisitPdfJobOptions;

  constructor(
    private siteVisitPdfService: SiteVisitPdfService,
    private projectService: ProjectService,
    private loaderService: LoaderService,
    private snackBar: MatSnackBar
  ) {}

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  runJob(startRequest: Observable<unknown>, options: SiteVisitPdfJobOptions): void {
    this.activeOptions = options;
    this.loaderService.isLoading.next(true);
    startRequest.subscribe({
      next: (res) => this.handleStartResponse(res),
      error: (err) => {
        console.error(err);
        this.loaderService.isLoading.next(false);
        this.snackBar.open('Something went wrong!', 'Ok', { duration: 3000 });
      },
    });
  }

  dismissToast(): void {
    this.pollSub?.unsubscribe();
    this.toast$.next(HIDDEN_TOAST);
  }

  downloadAgain(): void {
    const url = this.toast$.value.downloadUrl?.trim();
    if (!url) {
      return;
    }
    this.downloadPdf(url, this.activeOptions?.fileName);
  }

  tryAgain(): void {
    const options = this.activeOptions;
    if (!options) {
      return;
    }
    this.pollSub?.unsubscribe();
    this.toast$.next({
      status: 'preparing',
      message: options.preparingMessage ?? 'Preparing your report…',
      downloadUrl: '',
    });
    this.loaderService.isLoading.next(true);
    options.retry().subscribe({
      next: (res) => this.handleStartResponse(res),
      error: (err) => {
        console.error(err);
        this.loaderService.isLoading.next(false);
        this.toast$.next({
          status: 'failed',
          message: 'Something went wrong. Please try again.',
          downloadUrl: '',
        });
      },
    });
  }

  private handleStartResponse(res: unknown): void {
    const options = this.activeOptions;
    if (!options) {
      return;
    }

    const pdfUrl = this.siteVisitPdfService.extractReportPdfUrl(res);
    if (pdfUrl) {
      this.loaderService.isLoading.next(false);
      this.dismissToast();
      this.applyUrlAction(
        pdfUrl,
        options.immediateUrlAction ?? 'download',
        options
      );
      return;
    }

    const pdfId = this.siteVisitPdfService.extractPdfJobId(res);
    if (pdfId) {
      this.loaderService.isLoading.next(false);
      this.startPolling(pdfId, options);
      return;
    }

    this.loaderService.isLoading.next(false);
    this.toast$.next({
      status: 'failed',
      message: 'Could not start report generation. Please try again.',
      downloadUrl: '',
    });
    this.snackBar.open(
      'Could not start report generation. Please try again.',
      'Ok',
      { duration: 5000 }
    );
  }

  private startPolling(pdfId: string, options: SiteVisitPdfJobOptions): void {
    this.pollSub?.unsubscribe();
    this.toast$.next({
      status: 'preparing',
      message: options.preparingMessage ?? 'Preparing your report…',
      downloadUrl: '',
    });

    this.pollSub = this.siteVisitPdfService.pollUntilDone(pdfId).subscribe({
      next: (result) => {
        if (result.status === 'SUCCESS' && result.downloadUrl?.trim()) {
          const url = result.downloadUrl.trim();
          this.toast$.next({
            status: 'success',
            message: 'Your report is ready.',
            downloadUrl: url,
          });
          this.applyUrlAction(
            url,
            options.polledSuccessAction ?? 'download',
            options
          );
          return;
        }

        this.toast$.next({
          status: 'failed',
          message:
            result.message || 'PDF generation failed. Please try again.',
          downloadUrl: '',
        });
      },
      error: () => {
        this.toast$.next({
          status: 'failed',
          message: 'PDF generation failed. Please try again.',
          downloadUrl: '',
        });
      },
    });
  }

  private applyUrlAction(
    url: string,
    action: SiteVisitPdfUrlAction,
    options: SiteVisitPdfJobOptions
  ): void {
    if (action === 'preview' && options.onPreviewUrl) {
      options.onPreviewUrl(url);
      return;
    }
    this.downloadPdf(url, options.fileName);
  }

  private downloadPdf(url: string, fileName?: string): void {
    const name = fileName?.trim() || 'site-visit-report.pdf';
    this.projectService.downloadFile(url).subscribe({
      next: (data: Blob) => {
        const blob = new Blob([data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      },
      error: (err) => {
        console.error('PDF download failed', err);
        this.snackBar.open(
          'Download failed. Use Download Again from the notification.',
          'Ok',
          { duration: 5000 }
        );
      },
    });
  }
}
